/**
 * Configuração avançada por tarefa (OpenSpec #2.7 §7).
 *
 * Collapsible section abaixo da config legacy do AISettingsTab. Mostra 3
 * cards (Rápido / Padrão / Qualidade) com provider + model + botão Testar.
 * Quando o tenant não configura nada, todos os tiers usam o
 * provider/model legado da org.
 */
import React, { useEffect, useState } from 'react';
import { AIProvider, ModelTier, ProvidersByTier, TierConfig } from '../../types';
import { apiService } from '../../services/api';

type AxiosLike = { get<T>(url: string): Promise<{ data: T }>; post<T>(url: string, body: unknown): Promise<{ data: T }> };

function client(): AxiosLike {
  return (apiService as unknown as { api: AxiosLike }).api;
}

interface PricingEntry {
  input_per_1k_usd: number;
  output_per_1k_usd: number;
}

interface PolicyResponse {
  policy: {
    enabled_providers: AIProvider[];
    default_tier_mapping: Record<ModelTier, { provider: AIProvider; model: string }>;
    pricing_reference: Record<string, Record<string, PricingEntry>>;
  } | null;
  bedrockEnabled: boolean;
}

interface TestResultOk {
  ok: true;
  latencyMs: number;
  inputTokens: number;
  outputTokens: number;
  modelIdEffective: string;
  estimatedCost: number;
  providerId: string;
}
interface TestResultFail {
  ok: false;
  error: { code: string; message: string };
  provider: { providerId: string; modelId: string };
}
type TestResult = TestResultOk | TestResultFail;

const TIERS: { id: ModelTier; label: string; hint: string }[] = [
  { id: 'fast', label: 'Rápido', hint: 'Extração / classificação — barato e veloz' },
  { id: 'standard', label: 'Padrão', hint: 'Decisões e qualificação de turno' },
  { id: 'quality', label: 'Qualidade', hint: 'Conversa final com o cliente' },
];

const PROVIDER_LABEL: Record<AIProvider, string> = {
  openai: 'OpenAI',
  azure: 'Azure OpenAI',
  anthropic: 'Anthropic',
  google: 'Google AI',
  bedrock: 'AWS Bedrock',
};

interface Props {
  value: ProvidersByTier | undefined;
  onChange: (next: ProvidersByTier | undefined) => void;
  canEdit: boolean;
}

export const PerTierConfigSection: React.FC<Props> = ({ value, onChange, canEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const [policy, setPolicy] = useState<PolicyResponse | null>(null);
  const [testStatus, setTestStatus] = useState<Record<ModelTier, TestResult | 'pending' | null>>({
    fast: null,
    standard: null,
    quality: null,
  });

  useEffect(() => {
    if (!expanded || policy) return;
    client()
      .get<PolicyResponse>('/ia/llm-config/policy')
      .then((r) => setPolicy(r.data))
      .catch(() => {
        // Non-fatal; just leave defaults disabled.
      });
  }, [expanded, policy]);

  function modelsFor(provider: AIProvider): string[] {
    if (!policy?.policy?.pricing_reference?.[provider]) return [];
    return Object.keys(policy.policy.pricing_reference[provider]);
  }

  function updateTier(tier: ModelTier, patch: Partial<TierConfig> | null): void {
    if (patch === null) {
      const next: ProvidersByTier = { ...(value || {}) };
      delete next[tier];
      onChange(Object.keys(next).length === 0 ? undefined : next);
      return;
    }
    const current = value?.[tier];
    const merged: TierConfig = {
      provider: patch.provider ?? current?.provider ?? policy?.policy?.default_tier_mapping[tier].provider ?? 'openai',
      model: patch.model ?? current?.model,
    };
    onChange({ ...(value || {}), [tier]: merged });
  }

  async function runTest(tier: ModelTier): Promise<void> {
    const cfg = value?.[tier];
    if (!cfg?.provider) return;
    setTestStatus((s) => ({ ...s, [tier]: 'pending' }));
    try {
      const res = await client().post<TestResult>('/ia/llm-config/test', {
        tier,
        provider: cfg.provider,
        model: cfg.model,
      });
      setTestStatus((s) => ({ ...s, [tier]: res.data }));
    } catch (err) {
      setTestStatus((s) => ({
        ...s,
        [tier]: { ok: false, error: { code: 'http_error', message: String(err) }, provider: { providerId: cfg.provider, modelId: cfg.model || '' } },
      }));
    }
  }

  const enabledProviders = policy?.policy?.enabled_providers || (['openai', 'azure', 'anthropic', 'google', 'bedrock'] as AIProvider[]);
  const bedrockDisabled = policy ? !policy.bedrockEnabled : false;

  return (
    <div style={{ marginTop: 16 }}>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        style={{
          padding: '10px 14px',
          background: 'transparent',
          border: '1px solid #cbd5e1',
          borderRadius: 8,
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
          color: '#334155',
        }}
      >
        {expanded ? '▼' : '▶'} Configuração avançada por tarefa
        <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 8, fontSize: 12 }}>
          Use modelos diferentes para extração, decisão e conversação (opcional)
        </span>
      </button>

      {expanded && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 12,
            marginTop: 12,
          }}
        >
          {TIERS.map((t) => {
            const cfg = value?.[t.id];
            const provider = cfg?.provider || (policy?.policy?.default_tier_mapping[t.id].provider ?? 'openai');
            const model = cfg?.model || (policy?.policy?.default_tier_mapping[t.id].model ?? '');
            const using = cfg ? 'override' : 'default';
            const status = testStatus[t.id];
            const pricing = policy?.policy?.pricing_reference?.[provider]?.[model];
            return (
              <div
                key={t.id}
                style={{
                  border: '1px solid ' + (using === 'override' ? '#c7d2fe' : '#e2e8f0'),
                  borderRadius: 8,
                  padding: 12,
                  background: using === 'override' ? '#eef2ff' : '#fff',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{t.label}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>{t.hint}</div>
                {using === 'default' && (
                  <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 6 }}>
                    Padrão do sistema
                  </div>
                )}
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 2 }}>
                  Provider
                </label>
                <select
                  value={provider}
                  disabled={!canEdit}
                  onChange={(e) => updateTier(t.id, { provider: e.target.value as AIProvider })}
                  style={{ width: '100%', padding: '4px 6px', fontSize: 12, marginBottom: 8 }}
                >
                  {enabledProviders.map((p) => (
                    <option
                      key={p}
                      value={p}
                      disabled={p === 'bedrock' && bedrockDisabled}
                      title={p === 'bedrock' && bedrockDisabled ? 'Temporariamente indisponível' : undefined}
                    >
                      {PROVIDER_LABEL[p]}{p === 'bedrock' && bedrockDisabled ? ' (indisponível)' : ''}
                    </option>
                  ))}
                </select>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 2 }}>
                  Modelo
                </label>
                <select
                  value={model}
                  disabled={!canEdit}
                  onChange={(e) => updateTier(t.id, { model: e.target.value })}
                  style={{ width: '100%', padding: '4px 6px', fontSize: 12, marginBottom: 8 }}
                >
                  <option value="">(padrão do provider)</option>
                  {modelsFor(provider).map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                {pricing && (
                  <div style={{ fontSize: 10, color: '#64748b', marginBottom: 6 }}>
                    ${pricing.input_per_1k_usd.toFixed(5)}/1k in · ${pricing.output_per_1k_usd.toFixed(5)}/1k out
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => runTest(t.id)}
                    disabled={!canEdit || !cfg || status === 'pending'}
                    style={{
                      flex: 1,
                      padding: '4px 8px',
                      fontSize: 11,
                      border: '1px solid #cbd5e1',
                      borderRadius: 4,
                      background: '#fff',
                      cursor: canEdit && cfg ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {status === 'pending' ? 'Testando…' : 'Testar'}
                  </button>
                  {using === 'override' && canEdit && (
                    <button
                      type="button"
                      onClick={() => updateTier(t.id, null)}
                      style={{
                        padding: '4px 8px',
                        fontSize: 11,
                        border: 0,
                        background: 'transparent',
                        color: '#dc2626',
                        cursor: 'pointer',
                      }}
                    >
                      Resetar
                    </button>
                  )}
                </div>
                {status && status !== 'pending' && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: '6px 8px',
                      borderRadius: 4,
                      background: status.ok ? '#ecfdf5' : '#fef2f2',
                      color: status.ok ? '#065f46' : '#b91c1c',
                      fontSize: 11,
                    }}
                  >
                    {status.ok
                      ? `✓ ${status.latencyMs}ms · $${status.estimatedCost.toFixed(5)}`
                      : `✗ ${status.error.code}: ${status.error.message.slice(0, 60)}`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
